import React, { useState, useEffect, useRef } from 'react';
import { 
  ShoppingCart, 
  Plus, 
  Eye, 
  Search,
  Filter,
  MoreVertical,
  DollarSign,
  Calendar,
  User,
  Mail,
  CheckCircle,
  Clock,
  AlertCircle,
  Download,
  Edit,
  RefreshCw,
  FileText,
  TrendingUp,
  TrendingDown,
  X
} from 'lucide-react';
import { Row, Col, Button, Form, Table, Pagination, Card, Badge, InputGroup, Dropdown, Modal, Alert, Spinner } from 'react-bootstrap';
import './Dashboard.css';

const Retail = () => {
  const [retailQuotes, setRetailQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  
  // Enhanced filters
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    payment: 'all',
    customer: '',
    dateFrom: '',
    dateTo: '',
    amountMin: '',
    amountMax: ''
  });
  
  // Modal states
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  // Enhanced stats
  const [stats, setStats] = useState({
    total: 0,
    paid: 0,
    pending: 0,
    invoiced: 0,
    totalValue: 0,
    avgValue: 0
  });

  // Handle items per page change
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    fetchRetailQuotes(1);
  };

  const filtersFirstRun = useRef(true);

  // Fetch retail quotes (server applies filters to full dataset, then paginates)
  const fetchRetailQuotes = async (page = 1) => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: itemsPerPage.toString(),
        ...Object.fromEntries(Object.entries(filters).filter(([_, value]) => value !== '' && value !== 'all'))
      });

      const token = localStorage.getItem('token');
      const response = await fetch(`/api/retail?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch retail quotes');
      }

      const data = await response.json();
      setRetailQuotes(data.data || []);
      setCurrentPage(data.pagination.page);
      setTotalPages(data.pagination.pages);
      setTotalItems(data.pagination.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Calculate quote total
  const calculateQuoteTotal = (quote) => {
    if (!quote.items || !Array.isArray(quote.items)) return 0;
    return quote.items.reduce((total, item) => {
      const itemTotal = (item.qty || 0) * (item.price || 0);
      return total + itemTotal;
    }, 0);
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/retail/stats', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data.data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    if (filtersFirstRun.current) {
      filtersFirstRun.current = false;
      fetchRetailQuotes(1);
      return;
    }
    const t = setTimeout(() => fetchRetailQuotes(1), 400);
    return () => clearTimeout(t);
  }, [filters]);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      search: '',
      status: 'all',
      payment: 'all',
      customer: '',
      dateFrom: '',
      dateTo: '',
      amountMin: '',
      amountMax: ''
    });
  };

  // Refresh data
  const refreshData = async () => {
    setRefreshing(true);
    try {
      await Promise.all([fetchRetailQuotes(currentPage), fetchStats()]);
    } finally {
      setRefreshing(false);
    }
  };

  // Handle page change
  const handlePageChange = (page) => {
    fetchRetailQuotes(page);
  };

  // View quote details
  const viewQuoteDetails = async (quoteId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/retail/${quoteId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedQuote(data.data);
        setShowDetailsModal(true);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Get status badge variant
  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'Retail': return 'primary';
      case 'Retail w/ Detailing': return 'info';
      case 'Invoiced': return 'success';
      case 'Deadsale': return 'secondary';
      default: return 'light';
    }
  };

  // Get payment status badge
  const getPaymentBadge = (paid, invoiced) => {
    if (invoiced) return <Badge bg="success">Invoiced</Badge>;
    if (paid) return <Badge bg="success">Paid</Badge>;
    return <Badge bg="warning">Pending</Badge>;
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading && retailQuotes.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">
            <ShoppingCart className="me-2" />
            Retail Quotes
          </h2>
          <p className="text-muted mb-0">Manage retail sales and quotes</p>
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="outline-secondary"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="me-1" size={16} />
            Filters
          </Button>
          <Button
            variant="outline-primary"
            onClick={refreshData}
            disabled={refreshing}
          >
            <RefreshCw className={`me-1 ${refreshing ? 'spin' : ''}`} size={16} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button variant="primary">
            <Plus className="me-1" size={16} />
            New Quote
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col md={2}>
          <Card className="text-center modern-stat-card">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-center mb-2">
                <ShoppingCart className="text-primary me-2" size={24} />
                <h4 className="mb-0">{stats.total}</h4>
              </div>
              <p className="text-muted mb-0 small">Total Quotes</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center modern-stat-card">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-center mb-2">
                <CheckCircle className="text-success me-2" size={24} />
                <h4 className="mb-0">{stats.paid}</h4>
              </div>
              <p className="text-muted mb-0 small">Paid</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center modern-stat-card">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-center mb-2">
                <Clock className="text-warning me-2" size={24} />
                <h4 className="mb-0">{stats.pending}</h4>
              </div>
              <p className="text-muted mb-0 small">Pending</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center modern-stat-card">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-center mb-2">
                <FileText className="text-info me-2" size={24} />
                <h4 className="mb-0">{stats.invoiced}</h4>
              </div>
              <p className="text-muted mb-0 small">Invoiced</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center modern-stat-card">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-center mb-2">
                <DollarSign className="text-success me-2" size={24} />
                <h4 className="mb-0">{formatCurrency(stats.totalValue)}</h4>
              </div>
              <p className="text-muted mb-0 small">Total Value</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center modern-stat-card">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-center mb-2">
                <TrendingUp className="text-primary me-2" size={24} />
                <h4 className="mb-0">{formatCurrency(stats.avgValue)}</h4>
              </div>
              <p className="text-muted mb-0 small">Avg Value</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Enhanced Filters */}
      {showFilters && (
        <Card className="mb-4 modern-card">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <Filter className="me-2" size={18} />
              Advanced Filters
            </h5>
            <div className="d-flex gap-2">
              <Button variant="outline-secondary" size="sm" onClick={clearFilters}>
                <X className="me-1" size={14} />
                Clear All
              </Button>
              <Button variant="outline-primary" size="sm" onClick={() => setShowFilters(false)}>
                Hide Filters
              </Button>
            </div>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Search</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <Search size={16} />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Search by quote #, customer, contact, or email..."
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <option value="all">All Statuses</option>
                    <option value="Retail">Retail</option>
                    <option value="Invoiced">Invoiced</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Payment Status</Form.Label>
                  <Form.Select
                    value={filters.payment}
                    onChange={(e) => handleFilterChange('payment', e.target.value)}
                  >
                    <option value="all">All Payment Status</option>
                    <option value="paid">Paid</option>
                    <option value="pending">Pending</option>
                    <option value="invoiced">Invoiced</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Customer</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Filter by customer name..."
                    value={filters.customer}
                    onChange={(e) => handleFilterChange('customer', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>From Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>To Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Amount Range</Form.Label>
                  <div className="d-flex gap-2">
                    <Form.Control
                      type="number"
                      placeholder="Min"
                      value={filters.amountMin}
                      onChange={(e) => handleFilterChange('amountMin', e.target.value)}
                    />
                    <Form.Control
                      type="number"
                      placeholder="Max"
                      value={filters.amountMax}
                      onChange={(e) => handleFilterChange('amountMax', e.target.value)}
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" className="mb-4">
          <AlertCircle className="me-2" />
          {error}
        </Alert>
      )}

      {/* Enhanced Retail Quotes Table */}
      <Card className="modern-card">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-0">
              <ShoppingCart className="me-2" size={18} />
              Retail Quotes ({totalItems})
            </h5>
            {Object.values(filters).some(value => value && value !== 'all') && (
              <small className="text-muted">Filtered results</small>
            )}
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-primary" size="sm">
              <Download className="me-1" size={14} />
              Export
            </Button>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
              <p className="mt-3 text-muted">Loading retail quotes...</p>
            </div>
          ) : (
            <Table responsive hover className="mb-0 modern-table">
              <thead className="table-light">
                <tr>
                  <th>Quote #</th>
                  <th>Customer</th>
                  <th>Contact</th>
                  <th>Status</th>
                  <th>Payment</th>
                  <th>Items</th>
                  <th>Total Value</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {retailQuotes.map((quote) => (
                <tr key={quote.id}>
                  <td>
                    <strong>{quote.project?.estimateNumber || 'N/A'}</strong>
                  </td>
                  <td>
                    <div>
                      <div className="fw-medium">{quote.project?.customerCompany || 'N/A'}</div>
                      <small className="text-muted">{quote.project?.projectName || ''}</small>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div className="d-flex align-items-center">
                        <User size={14} className="me-1" />
                        {quote.attn || 'N/A'}
                      </div>
                      <div className="d-flex align-items-center">
                        <Mail size={14} className="me-1" />
                        {quote.email ? (
                          <a 
                            href={`mailto:${quote.email}`} 
                            style={{ color: '#0d6efd', textDecoration: 'none' }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <small>{quote.email}</small>
                          </a>
                        ) : (
                          <small className="text-muted">N/A</small>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <Badge bg={getStatusBadgeVariant(quote.project?.status)}>
                      {quote.project?.status || 'N/A'}
                    </Badge>
                  </td>
                  <td>
                    {getPaymentBadge(quote.paid, quote.invoiced)}
                  </td>
                  <td>
                    <span className="fw-medium">{quote.items?.length || 0}</span>
                  </td>
                  <td>
                    <div className="fw-medium text-success">
                      {formatCurrency(calculateQuoteTotal(quote))}
                    </div>
                  </td>
                  <td>
                    <div>
                      <div>{formatDate(quote.createdAt)}</div>
                      {quote.submitted && (
                        <small className="text-success">
                          Submitted: {formatDate(quote.submitted)}
                        </small>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="d-flex gap-1">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => viewQuoteDetails(quote.id)}
                      >
                        <Eye size={14} />
                      </Button>
                      <Button variant="outline-secondary" size="sm">
                        <Edit size={14} />
                      </Button>
                      <Dropdown>
                        <Dropdown.Toggle variant="outline-secondary" size="sm">
                          <MoreVertical size={14} />
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item>Generate PDF</Dropdown.Item>
                          <Dropdown.Item>Send Email</Dropdown.Item>
                          <Dropdown.Divider />
                          <Dropdown.Item className="text-danger">Delete</Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                  </td>
                </tr>
              ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
        <Card.Footer>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <div className="text-muted">
                Showing {totalItems === 0 ? 0 : ((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} entries
              </div>
              {/* Items per page selector */}
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted" style={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                  Show:
                </span>
                <Form.Select
                  value={itemsPerPage}
                  onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value, 10))}
                  size="sm"
                  style={{ width: 'auto', minWidth: '80px' }}
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Form.Select>
                <span className="text-muted" style={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                  per page
                </span>
              </div>
            </div>
            
            {/* Pagination controls */}
            {totalPages > 1 && (
              <Pagination className="mb-0">
                <Pagination.First 
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                />
                <Pagination.Prev 
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                />
                
                {/* Show page numbers with ellipsis */}
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                  // Show first 3 pages, last 3 pages, and current page with context
                  const shouldShow = 
                    page <= 3 || 
                    page >= totalPages - 2 || 
                    Math.abs(page - currentPage) <= 1;
                  
                  if (!shouldShow) {
                    // Show ellipsis for gaps
                    if (page === 4 && currentPage > 5) {
                      return <Pagination.Ellipsis key={`ellipsis-${page}`} />;
                    }
                    if (page === totalPages - 3 && currentPage < totalPages - 4) {
                      return <Pagination.Ellipsis key={`ellipsis-${page}`} />;
                    }
                    return null;
                  }
                  
                  return (
                    <Pagination.Item
                      key={page}
                      active={page === currentPage}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </Pagination.Item>
                  );
                })}
                
                <Pagination.Next 
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                />
                <Pagination.Last 
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                />
              </Pagination>
            )}
          </div>
        </Card.Footer>
      </Card>

      {/* Quote Details Modal */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            <ShoppingCart className="me-2" />
            Quote Details - {selectedQuote?.project?.estimateNumber}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedQuote && (
            <div>
              {/* Quote Header */}
              <Row className="mb-4">
                <Col md={6}>
                  <h5>Customer Information</h5>
                  <p className="mb-1"><strong>Company:</strong> {selectedQuote.project?.customerCompany}</p>
                  <p className="mb-1"><strong>Project:</strong> {selectedQuote.project?.projectName}</p>
                  <p className="mb-1"><strong>Contact:</strong> {selectedQuote.attn}</p>
                  <p className="mb-0">
                    <strong>Email:</strong>{' '}
                    {selectedQuote.email ? (
                      <a 
                        href={`mailto:${selectedQuote.email}`} 
                        style={{ color: '#0d6efd', textDecoration: 'none' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {selectedQuote.email}
                      </a>
                    ) : (
                      <span className="text-muted">N/A</span>
                    )}
                  </p>
                </Col>
                <Col md={6}>
                  <h5>Quote Information</h5>
                  <p className="mb-1"><strong>Status:</strong> 
                    <Badge bg={getStatusBadgeVariant(selectedQuote.project?.status)} className="ms-2">
                      {selectedQuote.project?.status}
                    </Badge>
                  </p>
                  <p className="mb-1"><strong>Payment Terms:</strong> {selectedQuote.paymentTerms || 'N/A'}</p>
                  <p className="mb-1"><strong>Tax Rate:</strong> {selectedQuote.taxRate || 0}%</p>
                  <p className="mb-0"><strong>Created:</strong> {formatDate(selectedQuote.createdAt)}</p>
                </Col>
              </Row>

              {/* Items Table */}
              <h5>Quote Items</h5>
              <Table responsive>
                <thead>
                  <tr>
                    <th>Item #</th>
                    <th>Description</th>
                    <th>Qty</th>
                    <th>UOM</th>
                    <th>Unit Price</th>
                    <th>Taxable</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedQuote.items?.map((item, index) => (
                    <tr key={index}>
                      <td>{item.itemNumber || 'N/A'}</td>
                      <td>{item.description || 'N/A'}</td>
                      <td>{item.qty || 0}</td>
                      <td>{item.uom || 'N/A'}</td>
                      <td>{formatCurrency(item.price)}</td>
                      <td>
                        <Badge bg={item.isTaxable ? 'success' : 'secondary'}>
                          {item.isTaxable ? 'Yes' : 'No'}
                        </Badge>
                      </td>
                      <td>{formatCurrency((item.qty || 0) * (item.price || 0))}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {/* Payment Information */}
              {selectedQuote.paid && (
                <Row className="mt-4">
                  <Col md={12}>
                    <h5>Payment Information</h5>
                    <p className="mb-1"><strong>Payment Type:</strong> {selectedQuote.paymentType || 'N/A'}</p>
                    <p className="mb-1"><strong>Amount:</strong> {formatCurrency(selectedQuote.paymentAmount)}</p>
                    <p className="mb-1"><strong>Paid Date:</strong> {formatDate(selectedQuote.paidDate)}</p>
                    {selectedQuote.paymentNote && (
                      <p className="mb-0"><strong>Note:</strong> {selectedQuote.paymentNote}</p>
                    )}
                  </Col>
                </Row>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
          <Button variant="primary">
            <Download className="me-1" size={16} />
            Download PDF
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Retail;



